/**
 * useFeatureFlag — PostHog feature-flag hook for the web app.
 *
 * Returns `defaultValue` until flags load (and forever when PostHog is
 * disabled/blocked), then tracks the live flag value. Flags are managed
 * in PostHog → Feature Flags (`starter-picks`, `top-movers`), so features
 * can be toggled or A/B-tested WITHOUT a redeploy.
 */
import { useEffect, useState } from 'react';
import { posthog } from '../posthog.config';

export function useFeatureFlag(key, defaultValue = true) {
  const read = () => {
    try {
      const v = posthog?.isFeatureEnabled?.(key);
      return v === undefined ? defaultValue : !!v;
    } catch { return defaultValue; }
  };
  const [value, setValue] = useState(read);
  useEffect(() => {
    let unsub;
    try { unsub = posthog?.onFeatureFlags?.(() => setValue(read())); } catch { /* inert */ }
    return () => { try { if (typeof unsub === 'function') unsub(); } catch { /* inert */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return value;
}

/**
 * useFeatureFlagVariant — reads a MULTIVARIATE PostHog flag and returns its
 * active variant key (e.g. 'control' | 'action'), or `defaultVariant` until
 * flags load / when PostHog is blocked. Reading the flag emits the
 * `$feature_flag_called` exposure event that A/B experiments attribute on.
 * Used for the `starter-picks-cta` copy experiment — the app ships every
 * variant and PostHog decides which to show, so the split is re-tunable from
 * the dashboard with NO redeploy.
 */
export function useFeatureFlagVariant(key, defaultVariant = 'control') {
  const read = () => {
    try {
      const v = posthog?.getFeatureFlag?.(key);
      return (v === undefined || v === null) ? defaultVariant : String(v);
    } catch { return defaultVariant; }
  };
  const [variant, setVariant] = useState(read);
  useEffect(() => {
    let unsub;
    try { unsub = posthog?.onFeatureFlags?.(() => setVariant(read())); } catch { /* inert */ }
    return () => { try { if (typeof unsub === 'function') unsub(); } catch { /* inert */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return variant;
}
